import {
  Check,
  Monitor,
  Moon,
  Sun,
  RotateCcw,
  PanelLeft,
  PanelLeftClose,
  PanelLeftOpen,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTheme } from '@/context/ThemeContext';
import {
  PRIMARY_SWATCHES,
  SECONDARY_SWATCHES,
  SURFACE_SWATCHES,
  type Swatch,
} from '@/lib/appearance';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
      {children}
    </div>
  );
}

function SwatchRow({
  swatches,
  selected,
  onSelect,
  label,
}: {
  swatches: Swatch[];
  selected: string;
  onSelect: (name: string) => void;
  label: string;
}) {
  return (
    <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label={label}>
      {swatches.map((s) => {
        const isSelected = s.name === selected;
        const isDefault = s.hex === '';
        return (
          <button
            key={s.name}
            type="button"
            role="radio"
            aria-checked={isSelected}
            aria-label={s.name}
            title={s.name}
            onClick={() => onSelect(s.name)}
            className={cn(
              'relative h-7 w-7 rounded-md border transition-transform',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              isSelected ? 'border-foreground scale-110' : 'border-border hover:scale-105',
              // "Default" has no colour of its own, so show the page surface
              isDefault && 'bg-muted'
            )}
            style={isDefault ? undefined : { backgroundColor: s.hex }}
          >
            {isSelected && (
              <Check
                className={cn(
                  'absolute inset-0 m-auto h-3.5 w-3.5',
                  isDefault ? 'text-foreground' : 'text-white drop-shadow'
                )}
              />
            )}
          </button>
        );
      })}
    </div>
  );
}

function ModeButton({
  active,
  onClick,
  icon: Icon,
  children,
}: {
  active: boolean;
  onClick: () => void;
  icon: typeof Sun;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'flex flex-1 items-center justify-center gap-1.5 rounded-md border px-2 py-1.5 text-xs font-medium transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        active
          ? 'border-primary bg-primary/10 text-primary'
          : 'border-border text-muted-foreground hover:bg-muted'
      )}
    >
      <Icon className="h-3.5 w-3.5" />
      {children}
    </button>
  );
}

/**
 * Appearance controls. Every change writes straight through to the theme
 * context, which persists it and re-applies the CSS variables — so the effect
 * is visible immediately rather than behind a save step.
 */
export function AppearancePanel() {
  const { theme, setTheme, appearance, setAppearance, resetAppearance } = useTheme();

  return (
    <div className="w-72 space-y-4 p-3">
      <div className="space-y-2">
        <SectionLabel>Mode</SectionLabel>
        <div className="flex gap-1.5">
          <ModeButton active={theme === 'light'} onClick={() => setTheme('light')} icon={Sun}>
            Light
          </ModeButton>
          <ModeButton active={theme === 'dark'} onClick={() => setTheme('dark')} icon={Moon}>
            Dark
          </ModeButton>
          <ModeButton active={theme === 'system'} onClick={() => setTheme('system')} icon={Monitor}>
            Auto
          </ModeButton>
        </div>
      </div>

      <div className="space-y-2">
        <div className="flex items-baseline justify-between">
          <SectionLabel>Background tone</SectionLabel>
          <span className="text-[11px] text-muted-foreground">
            {appearance.backgroundTone <= 20
              ? 'Flat'
              : appearance.backgroundTone >= 80
                ? 'Deep'
                : 'Balanced'}
          </span>
        </div>
        {/* One slider drives the page surface; card, muted and border follow it,
            so the depth of the whole surface stack moves together. */}
        <Slider
          value={[appearance.backgroundTone]}
          onValueChange={([backgroundTone]) => setAppearance({ backgroundTone })}
          min={0}
          max={100}
          step={5}
          aria-label="Background tone"
        />
      </div>

      <div className="space-y-2">
        <SectionLabel>Primary colour</SectionLabel>
        <SwatchRow
          label="Primary colour"
          swatches={PRIMARY_SWATCHES}
          selected={appearance.primary}
          onSelect={(primary) => setAppearance({ primary })}
        />
      </div>

      <div className="space-y-2">
        <SectionLabel>Secondary colour</SectionLabel>
        <SwatchRow
          label="Secondary colour"
          swatches={SECONDARY_SWATCHES}
          selected={appearance.secondary}
          onSelect={(secondary) => setAppearance({ secondary })}
        />
      </div>

      <div className="space-y-2">
        <SectionLabel>Navigation bar</SectionLabel>
        <div className="flex gap-1.5">
          <ModeButton
            active={appearance.sidebarMode === 'light'}
            onClick={() => setAppearance({ sidebarMode: 'light' })}
            icon={Sun}
          >
            Light
          </ModeButton>
          <ModeButton
            active={appearance.sidebarMode === 'dark'}
            onClick={() => setAppearance({ sidebarMode: 'dark' })}
            icon={Moon}
          >
            Dark
          </ModeButton>
        </div>
        <SwatchRow
          label="Navigation bar colour"
          swatches={SURFACE_SWATCHES}
          selected={appearance.sidebarColor}
          onSelect={(sidebarColor) => setAppearance({ sidebarColor })}
        />
        <div className="flex gap-1.5">
          <ModeButton
            active={appearance.navWidth === 'collapsed'}
            onClick={() => setAppearance({ navWidth: 'collapsed' })}
            icon={PanelLeftClose}
          >
            Icons
          </ModeButton>
          <ModeButton
            active={appearance.navWidth === 'compact'}
            onClick={() => setAppearance({ navWidth: 'compact' })}
            icon={PanelLeft}
          >
            Compact
          </ModeButton>
          <ModeButton
            active={appearance.navWidth === 'expanded'}
            onClick={() => setAppearance({ navWidth: 'expanded' })}
            icon={PanelLeftOpen}
          >
            Full
          </ModeButton>
        </div>
      </div>

      <Button variant="ghost" size="sm" className="w-full gap-1.5" onClick={resetAppearance}>
        <RotateCcw className="h-3.5 w-3.5" />
        Reset to defaults
      </Button>
    </div>
  );
}
